import axios from 'axios'
import { getApiCommonHeaders, API_VERSIONS, DEFAULT_API_TIMEOUT_MS, API_BASE_URL, DEVICE_VALUE } from '@/config/api'
import { addSlugInterceptor } from '@/services/httpClient'
import { PROPERTY_FILTER_QUERY_MAP, serializeFilterList } from '@/config/propertyFilterQueryMap'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 20
const DEFAULT_PROPERTIES_ENDPOINT = `/${API_VERSIONS.PROPERTIES}/properties`
const DEFAULT_FILTER_OPTIONS_ENDPOINT = `/${API_VERSIONS.PROPERTIES}/properties/filters/options`
const DEFAULT_PROPERTY_DETAILS_ENDPOINT = `/${API_VERSIONS.PROPERTIES}/properties`

const PROPERTIES_ENDPOINT = import.meta.env.VITE_PROPERTIES_ENDPOINT || DEFAULT_PROPERTIES_ENDPOINT
const FILTER_OPTIONS_ENDPOINT = import.meta.env.VITE_FILTER_OPTIONS_ENDPOINT || DEFAULT_FILTER_OPTIONS_ENDPOINT
const PROPERTY_DETAILS_ENDPOINT = import.meta.env.VITE_PROPERTY_DETAILS_ENDPOINT || DEFAULT_PROPERTY_DETAILS_ENDPOINT
const REQUEST_TIMEOUT_MS = DEFAULT_API_TIMEOUT_MS

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: Number.isFinite(REQUEST_TIMEOUT_MS) ? REQUEST_TIMEOUT_MS : 10000,
  headers: getApiCommonHeaders()
})

addSlugInterceptor(apiClient)

const normalizeSlashes = (value = '') => String(value).replace(/\/+/g, '/').replace(/\/$/, '')

const basePathIncludesApiPrefix = (() => {
  const rawBase = String(API_BASE_URL || '').trim()
  if (!rawBase) {
    return false
  }

  try {
    const parsed = new URL(rawBase)
    const normalizedPath = normalizeSlashes(parsed.pathname || '')
    return normalizedPath === '/api' || normalizedPath.endsWith('/api')
  } catch {
    const normalizedBase = normalizeSlashes(rawBase)
    return normalizedBase === '/api' || normalizedBase.endsWith('/api')
  }
})()

const normalizeEndpointTail = (endpoint, fallback) => {
  const source = String(endpoint || fallback || '').trim()
  const pathOnly = source
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/[?#].*$/, '')

  const segments = pathOnly
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)

  // Normalize both "/api/..." and "/..." endpoint styles to a single internal tail.
  while (segments[0]?.toLowerCase() === 'api') {
    segments.shift()
  }

  return segments.join('/')
}

const resolveEndpointPath = (endpoint, fallback) => {
  const tail = normalizeEndpointTail(endpoint, fallback)
  if (!tail) {
    return basePathIncludesApiPrefix ? '' : 'api'
  }

  return basePathIncludesApiPrefix ? tail : `api/${tail}`
}

const RESOLVED_PROPERTIES_ENDPOINT = resolveEndpointPath(PROPERTIES_ENDPOINT, DEFAULT_PROPERTIES_ENDPOINT)
const RESOLVED_FILTER_OPTIONS_ENDPOINT = resolveEndpointPath(
  FILTER_OPTIONS_ENDPOINT,
  DEFAULT_FILTER_OPTIONS_ENDPOINT
)
const RESOLVED_PROPERTY_DETAILS_ENDPOINT = resolveEndpointPath(
  PROPERTY_DETAILS_ENDPOINT,
  DEFAULT_PROPERTY_DETAILS_ENDPOINT
)

const toPositiveInteger = (value, fallback) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.max(1, Math.trunc(parsed))
}

const normalizeMetadata = (metadata = {}, fallbackPage = DEFAULT_PAGE, fallbackPerPage = DEFAULT_PER_PAGE) => {
  const currentPage = toPositiveInteger(
    metadata.current_page ?? metadata.currentPage,
    fallbackPage
  )

  const perPage = toPositiveInteger(
    metadata.per_page ?? metadata.perPage,
    fallbackPerPage
  )

  const total = Math.max(0, Number(metadata.total) || 0)
  const lastPage = toPositiveInteger(
    metadata.last_page ?? metadata.lastPage ?? metadata.total_pages ?? metadata.totalPages,
    currentPage
  )
  const from = Number.isFinite(Number(metadata.from)) ? Number(metadata.from) : 0
  const to = Number.isFinite(Number(metadata.to)) ? Number(metadata.to) : 0

  return {
    total,
    per_page: perPage,
    current_page: currentPage,
    last_page: Math.max(lastPage, currentPage),
    from,
    to
  }
}

const toStringValue = (value) => {
  if (value === undefined || value === null) {
    return ''
  }
  return String(value).trim()
}

const toNumberValue = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const extractItemsFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  if (Array.isArray(payload.data)) {
    return payload.data
  }

  if (Array.isArray(payload.items)) {
    return payload.items
  }

  if (Array.isArray(payload.properties)) {
    return payload.properties
  }

  return []
}

const normalizeOptionList = (items = []) => {
  const list = Array.isArray(items)
    ? items
    : items && typeof items === 'object'
      ? Object.entries(items).map(([key, value]) => {
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            return { key, ...value }
          }
          return { key, value }
        })
      : []
  const seen = new Set()
  const pickFirstText = (candidates = []) =>
    candidates.map((value) => toStringValue(value)).find((value) => value.length > 0) || ''
  const pickHumanText = (candidates = []) => {
    const normalized = candidates.map((value) => toStringValue(value)).filter(Boolean)
    const withLetters = normalized.find((value) => /[a-z]/i.test(value))
    return withLetters || normalized[0] || ''
  }
  const findHumanReadableValue = (value, depth = 0) => {
    if (depth > 3 || value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const text = toStringValue(value)
      return /[a-z]/i.test(text) ? text : ''
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const discovered = findHumanReadableValue(item, depth + 1)
        if (discovered) {
          return discovered
        }
      }
      return ''
    }

    if (typeof value === 'object') {
      for (const item of Object.values(value)) {
        const discovered = findHumanReadableValue(item, depth + 1)
        if (discovered) {
          return discovered
        }
      }
    }

    return ''
  }

  return list
    .map((item) => {
      if (typeof item === 'string') {
        const value = toStringValue(item)
        if (!value) {
          return null
        }
        return { id: value, name: value }
      }

      if (!item || typeof item !== 'object') {
        return null
      }

      let id = pickFirstText([
        item.value,
        item.Value,
        item.id,
        item.Id,
        item.code,
        item.Code,
        item.key,
        item.Key,
        item.name,
        item.Name,
        item.label,
        item.Label
      ])
      let name = pickHumanText([
        item.key,
        item.Key,
        item.displayLabel,
        item.display_label,
        item.displayName,
        item.display_name,
        item.label,
        item.Label,
        item.name,
        item.Name,
        item.text,
        item.Text,
        item.title,
        item.Title,
        item.id,
        item.Id,
        item.value,
        item.Value,
        item.code,
        item.Code
      ])
      const label = pickHumanText([
        item.key,
        item.Key,
        item.displayLabel,
        item.display_label,
        item.displayName,
        item.display_name,
        item.label,
        item.Label,
        item.name,
        item.Name,
        item.text,
        item.Text,
        item.title,
        item.Title
      ])

      if (!/[a-z]/i.test(name)) {
        const discoveredName = findHumanReadableValue(item)
        if (discoveredName) {
          name = discoveredName
        }
      }

      if (!id && !name) {
        return null
      }

      return {
        id: id || name,
        name: name || id,
        label: label || name || id
      }
    })
    .filter(Boolean)
    .filter((item) => {
      const key = `${item.id}::${item.name}`.toLowerCase()
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
}

const normalizeAvailabilityOptions = (payload = {}) => {
  const data = payload?.data && typeof payload.data === 'object' ? payload.data : {}

  // bookingTypes can be [{id,name}] objects or plain strings
  const rawBookingTypes = Array.isArray(data.bookingTypes) ? data.bookingTypes : []
  const seenIds = new Set()
  const bookingTypes = rawBookingTypes
    .map((item) => {
      if (item && typeof item === 'object' && item.id) {
        return { id: toStringValue(item.id).toUpperCase(), name: toStringValue(item.name) || toStringValue(item.id) }
      }
      const str = toStringValue(item).toUpperCase()
      return str ? { id: str, name: str } : null
    })
    .filter((item) => {
      if (!item || !item.id || seenIds.has(item.id)) return false
      seenIds.add(item.id)
      return true
    })

  const bookingTypeIds = bookingTypes.map((bt) => bt.id)
  const defaultPlanRaw = toStringValue(data.defaultPlan).toUpperCase()

  return {
    bookingTypes: bookingTypes.length > 0 ? bookingTypes : [{ id: 'MONTHLY', name: 'Monthly' }],
    defaultPlan: defaultPlanRaw && bookingTypeIds.includes(defaultPlanRaw)
      ? defaultPlanRaw
      : bookingTypeIds[0] || 'MONTHLY',
    currency: toStringValue(data.currency).toUpperCase() || 'INR',
    types: normalizeOptionList(data.types),
    categories: normalizeOptionList(data.categories),
    cities: normalizeOptionList(data.cities),
    areas: normalizeOptionList(data.areas),
    sharingTypes: normalizeOptionList(data.sharingTypes),
    roomTypePlans: normalizeOptionList(data.roomTypePlans)
  }
}

export const buildPropertiesApiParams = ({
  page = DEFAULT_PAGE,
  perPage = DEFAULT_PER_PAGE,
  filters = {}
} = {}) => {
  const safePage = toPositiveInteger(page, DEFAULT_PAGE)
  const safePerPage = Math.min(100, toPositiveInteger(perPage, DEFAULT_PER_PAGE))

  const params = {
    page: safePage,
    per_page: safePerPage
  }

  Object.keys(PROPERTY_FILTER_QUERY_MAP).forEach((stateKey) => {
    const fieldMap = PROPERTY_FILTER_QUERY_MAP[stateKey]
    const value = filters?.[stateKey]

    if (value === undefined || value === null || stateKey === 'page' || stateKey === 'perPage') {
      return
    }

    if (fieldMap.type === 'list') {
      const serialized = serializeFilterList(value)
      if (serialized) {
        params[fieldMap.apiKey] = serialized
      }
      return
    }

    if (fieldMap.type === 'number') {
      const numericValue = toNumberValue(value)
      if (numericValue !== null) {
        params[fieldMap.apiKey] = numericValue
      }
      return
    }

    const textValue = toStringValue(value)
    if (textValue) {
      params[fieldMap.apiKey] = textValue
    }
  })

  return params
}

export const fetchPropertiesFromApi = async ({
  page = DEFAULT_PAGE,
  perPage = DEFAULT_PER_PAGE,
  filters = {}
} = {}) => {
  const params = buildPropertiesApiParams({
    page,
    perPage,
    filters
  })

  const response = await apiClient.get(RESOLVED_PROPERTIES_ENDPOINT, {
    params
  })

  const payload = response?.data ?? {}
  const data = payload?.data || payload
  return {
    items: extractItemsFromPayload(data?.properties ? data : payload),
    metadata: normalizeMetadata(data?.pagination || payload.metadata, params.page, params.per_page)
  }
}

export const fetchFilterOptions = async () => {
  const response = await apiClient.get(RESOLVED_FILTER_OPTIONS_ENDPOINT)
  return normalizeAvailabilityOptions(response?.data ?? {})
}

export const fetchPropertyDetailsFromApi = async ({ propertyId } = {}) => {
  const safePropertyId = String(propertyId ?? '').trim()
  if (!safePropertyId) {
    throw new Error('Property ID is required to fetch details')
  }

  const detailsPath = [RESOLVED_PROPERTY_DETAILS_ENDPOINT, encodeURIComponent(safePropertyId)]
    .filter(Boolean)
    .join('/')

  const response = await apiClient.get(detailsPath)
  return response?.data?.data ?? null
}

export const fetchPropertyAvailability = async ({ propertyId, checkin, plan, checkout } = {}) => {
  const safePropertyId = String(propertyId ?? '').trim()
  if (!safePropertyId) {
    throw new Error('Property ID is required to fetch availability')
  }
  if (!checkin) {
    throw new Error('Check-in date is required')
  }
  if (!plan) {
    throw new Error('Plan (DAYWISE or MONTHLY) is required')
  }

  const availabilityPath = [RESOLVED_PROPERTY_DETAILS_ENDPOINT, encodeURIComponent(safePropertyId), 'availability']
    .filter(Boolean)
    .join('/')

  const params = {
    checkin,
    plan: String(plan).toUpperCase()
  }

  if (checkout) {
    params.checkout = checkout
  }

  const response = await apiClient.get(availabilityPath, { params })
  return response?.data?.data ?? null
}

export const formatApiError = (error) => {
  if (!error) {
    return 'Unknown API error'
  }

  if (error.response) {
    const status = error.response.status
    const statusText = error.response.statusText || 'Request failed'
    return `API ${status}: ${statusText}`
  }

  if (error.request) {
    return 'API is unreachable or timed out'
  }

  return String(error.message || 'Unexpected API error')
}
